import { useCallback, useEffect, useState } from "react";

import { clearDraft, loadDraft, saveDraft } from "../storage";

/**
 * Manages the in-progress sentence textbox and syncs it through
 * localStorage so an accidental refresh doesn't nuke typing. Drafts
 * are keyed by puzzle id, so yesterday's draft naturally falls away
 * when a new puzzle is loaded.
 *
 * The caller invokes `clear()` after a successful submit to drop the
 * just-scored sentence; the next mount will start blank.
 */
export interface UseDraftSentence {
  readonly sentence: string;
  readonly setSentence: (s: string) => void;
  readonly clear: () => void;
}

export function useDraftSentence(puzzleId: string | undefined): UseDraftSentence {
  const [sentence, setSentenceState] = useState("");

  // Hydrate from localStorage when the puzzle id changes (mount, or
  // navigation across day boundary).
  useEffect(() => {
    const next = puzzleId === undefined ? "" : loadDraft(puzzleId);
    // Bridging an external source into React state — see useDailyState
    // for the same pattern.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSentenceState(next);
  }, [puzzleId]);

  const setSentence = useCallback(
    (s: string) => {
      setSentenceState(s);
      if (puzzleId !== undefined) saveDraft(puzzleId, s);
    },
    [puzzleId],
  );

  const clear = useCallback(() => {
    setSentenceState("");
    if (puzzleId !== undefined) clearDraft(puzzleId);
  }, [puzzleId]);

  return { sentence, setSentence, clear };
}
